import {
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Button,
  Menu,
  Alert,
  Box,
  Typography,
  Checkbox,
  FormControl,
  FormGroup as MuiFormGroup,
  Badge,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  useMediaQuery,
} from "@mui/material";
import PeopleIcon from '@mui/icons-material/People';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { organizationsApi } from "api";
import {
  buildStatisticsV2Params,
  fetchStatisticsV2Current,
  fetchStatisticsV2History,
  mapHistoryV2ResponseToStartedProcesses,
  mapStatisticsV2TasksToStartedProcesses,
  newSortToApiSort,
  shouldSkipStatisticsV2Fetch,
  STATISTICS_V2_PROCESS_PAGE_SIZE,
} from "api/statisticsV2Api";
import type { StatisticsV2Pagination } from "model/statisticsModel";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ProcessesList from "./ProcessesList";
import { StartedProcess } from "./ProcessItem";
import { useForm, useOrganizationCalendarRevision, useSnackbar, useTranslations, useUser } from "hooks";
import { getTranslation, getStartedByMeProcessIds } from "common";
import FilterListIcon from "@mui/icons-material/FilterList";
import SortIcon from "@mui/icons-material/Sort";
import { Organization } from "model/organizationModel";
import { ProcessModelType } from "model/processModel";
import { SortCriterion } from "common/utilities";
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { SortState } from "hooks/useSortableList";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import CheckIcon from "@mui/icons-material/Check";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { User } from "./UserItem";
import { Group } from "./GroupsPanel";
import { Role } from "./RolesPanel";
import { Robot } from "hooks/useRobots";
import FlowentiButton from "./flowenti-ui-kit/FlowentiButton";
import MonitorSearchBar from "./MonitorSearchBar";
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { AccountCircle, LabelOutlined, Search } from "@mui/icons-material";
import Labels from "./labels/Labels";
import { LabelData } from "./labels/label_Interface";
import StartProcess from "./dialogs/StartProcess";
import MultiSelectSearch from "./MultiSelectSearch";
import DateRangePickerMenu, { DateRangePickerValue } from "./DateRangePickerMenu";
import dayjs from "dayjs";
import { keyBy } from "lodash";
import useSubscriptionFeatures from "hooks/useSubscriptionFeatures";
import { useNavigate } from "react-router-dom";
import ProcessExportButton from "./ProcessExportButton";
import SlaLiveKpiChips from "./sla/SlaLiveKpiChips";

interface Data<T> {
  loaded: boolean;
  data: T[];
}
interface PageData {
  users: Data<User>;
  robots: Data<Robot>;
  groups: Data<Group>;
  processes: Data<StartedProcess>;
  roles: Data<Role>;
  processModels: Data<ProcessModelType>;
  userDataKeys: Data<string>;
  tags: Data<LabelData>;
  priorities: Data<string>;
  allGroups: Data<string>;
  allRoles: Data<string>;
}
interface ProcessStartedProps {
  organization: Organization;
  history: boolean;
}

const customParse = (value: string,) => {
  if (!value) return null;

  // console.log(value, "value dentro customparse")
  try {
    return JSON.parse(value, (key, value) => {
      if ((key === "start" || key === "end") && typeof value === "string") {
        const data = dayjs(value)
        if (data.isValid()) {
          return data
        }
      }
      return value
    })
  } catch {
    return null;
  }
}

const FILTER_ARRAY_FIELDS = [
  "modelids",
  "activityids",
  "userids",
  "groupids",
  "roleids",
  "creatorids",
  "userDataKeys",
  "tags",
  "groups",
  "roles",
  "priorities",
];

const normalizeProcessFilters = (filters: any) => {
  if (!filters) return filters;

  return FILTER_ARRAY_FIELDS.reduce((normalized, field) => ({
    ...normalized,
    [field]: Array.isArray(normalized[field]) ? normalized[field] : [],
  }), { ...filters });
};

const getUniqueValues = (processes: StartedProcess[], field: 'jsontag' | 'jsongroups' | 'jsonroles' | 'priorityname') => {
  const values = new Set<string>();
  const priorityOrderMap = new Map<string, number>();
  processes.forEach((process) => {
    const rawValue = process[field];
    if (rawValue) {
      if (field === 'priorityname') {
        values.add(rawValue);
        if (process.priorityorder != null) {
          const existing = priorityOrderMap.get(rawValue);
          if (existing == null || process.priorityorder < existing) {
            priorityOrderMap.set(rawValue, process.priorityorder);
          }
        }
      } else {
        try {
          // Se è già un oggetto (axios lo parsa automaticamente se è JSON)
          const parsed = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;
          if (Array.isArray(parsed)) {
            parsed.forEach((item: any) => {
              const name = item.tag || item.groupname || item.rolename;
              if (name) values.add(name);
            });
          }
        } catch (e) {
          console.error(`Error parsing ${field}:`, e, rawValue);
        }
      }
    }
  });
  const result = Array.from(values);
  if (field === 'priorityname') {
    result.sort((a, b) => (priorityOrderMap.get(a) ?? 0) - (priorityOrderMap.get(b) ?? 0));
  }
  // if (field === 'jsontag') console.log("Extracted unique tags:", result);
  return result;
};

const getUniqueTags = (processes: StartedProcess[]): LabelData[] => {
  const tagsMap = new Map<string, LabelData>();
  processes.forEach((process) => {
    const rawValue = process.jsontag;
    if (rawValue) {
      try {
        const parsed = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;
        if (Array.isArray(parsed)) {
          parsed.forEach((item: any) => {
            if (item.tag) {
              tagsMap.set(item.tag, {
                id: item.idtag,
                name: item.tag,
                css: item.color,
              });
            }
          });
        }
      } catch (e) {
        console.error(`Error parsing jsontag:`, e, rawValue);
      }
    }
  });
  return Array.from(tagsMap.values());
};

const LOCAL_STORAGE_KEY = `processesFilters`;
const SORT_STORAGE_KEY = `processesSorts`;
const STATUS_TOGGLE_ACTIVE_SX = {
  bgcolor: "#d7fbfb",
  borderColor: "#d7fbfb",
  color: "#1e355f",
  boxShadow: "none",
  px: 2.5,
  py: 1,
  minHeight: 36,
  "&:hover": {
    bgcolor: "#c8f4f4",
    borderColor: "#c8f4f4",
    boxShadow: "none",
  },
  "& .MuiButton-startIcon": {
    mr: 1,
    color: "#1e9ca0",
  },
};
const STATUS_TOGGLE_INACTIVE_SX = {
  bgcolor: "#eef2f5",
  borderColor: "#eef2f5",
  color: "#9aa7b2",
  boxShadow: "none",
  px: 2.5,
  py: 1,
  minHeight: 36,
  "&:hover": {
    bgcolor: "#e4e9ed",
    borderColor: "#e4e9ed",
    color: "#7f8c97",
    boxShadow: "none",
  },
};
const getProcessStorageKey = (
  storageKey: string,
  userId: number,
  organizationId: Organization["id"],
  history: boolean
) => `${storageKey}${history ? "-history" : ""}-${userId}-${organizationId}`;
const DEFAULT_PROCESS_FILTERS = {
  status: "current",
  modelids: [],
  activityids: [],
  userids: [],
  groupids: [],
  roleids: [],
  creatorids: [],
  search: "",
  userDataKeys: [],
  tags: [],
  groups: [],
  roles: [],
  priorities: [],
  dateRange: { start: null, end: null },
  // SLA health of the live list (0/1/2), set by the KPI chips; null = no filter.
  slaHealth: null as number | null,
  // Kept for backward compatibility with old localStorage entries; not
  // used by any UI control. The "Creato da" filter is now a single
  // MultiSelectSearch that includes the current user as one of the
  // options.
  startedByMe: false,
};

const ProcessStarted: React.FC<ProcessStartedProps> = ({ organization, history }) => {
  const translations = useTranslations();
  const calendarRevision = useOrganizationCalendarRevision(organization?.id);
  const navigate = useNavigate();
  const { sendSnack } = useSnackbar();
  const user = useUser();
  const { bypassSubscription, liveProcessCanUse } = useSubscriptionFeatures();
  const filtersStorageKey = useMemo(
    () => user?.userId ? getProcessStorageKey(LOCAL_STORAGE_KEY, user.userId, organization.id, history) : "",
    [organization.id, history, user?.userId]
  );
  const sortsStorageKey = useMemo(
    () => user?.userId ? getProcessStorageKey(SORT_STORAGE_KEY, user.userId, organization.id, history) : "",
    [organization.id, history, user?.userId]
  );
  const [showAllGroups, setShowAllGroups] = useState(false);
  const [startProcessOpen, setStartProcessOpen] = useState(false);
  const [toDo, setToDo] = useState<boolean>(true);
  const [doing, setDoing] = useState<boolean>(true);
  const { values, changeHandler, setValues } = useForm(
    DEFAULT_PROCESS_FILTERS,
    []
  );

  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [sortAnchorEl, setSortAnchorEl] = useState<HTMLElement | null>(null);
  const [userDataMenuAnchorEl, setUserDataMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [tagsMenuAnchorEl, setTagsMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [participantsMenuAnchorEl, setParticipantsMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [prioritiesMenuAnchorEl, setPrioritiesMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [collapsedFilterAnchorEl, setCollapsedFilterAnchorEl] = useState<HTMLElement | null>(null);
  const isFilterCollapsed = useMediaQuery("(max-width:1280px)");

  const handleUserDataClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserDataMenuAnchorEl(event.currentTarget);
  };

  const handleTagsClick = (event: React.MouseEvent<HTMLElement>) => {
    setTagsMenuAnchorEl(event.currentTarget);
  };

  const handleParticipantsClick = (event: React.MouseEvent<HTMLElement>) => {
    setParticipantsMenuAnchorEl(event.currentTarget);
  };

  const handlePrioritiesClick = (event: React.MouseEvent<HTMLElement>) => {
    setPrioritiesMenuAnchorEl(event.currentTarget);
  };

  const handleCollapsedFilterSelect = (
    setter: React.Dispatch<React.SetStateAction<HTMLElement | null>>
  ) => {
    if (collapsedFilterAnchorEl) {
      setter(collapsedFilterAnchorEl);
    }
    setCollapsedFilterAnchorEl(null);
  };

  const handleCloseUserData = () => {
    setUserDataMenuAnchorEl(null);
  };

  const handleCloseTags = () => {
    setTagsMenuAnchorEl(null);
  };

  const handleCloseParticipants = () => {
    setParticipantsMenuAnchorEl(null);
  };

  const handleClosePriorities = () => {
    setPrioritiesMenuAnchorEl(null);
  };

  const [activities, setActivities] = useState<Array<string | null>>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [newSort, setNewSort] = useState<SortCriterion>({});
  const [sortField, setSortField] = useState<string | null>(null);
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false);
  /** Incrementato a ogni rilettura della prima pagina: rilegge i KPI SLA. */
  const [slaKpiRefreshKey, setSlaKpiRefreshKey] = useState(0);
  /** SLA dell'organizzazione attivo (letto da `SlaLiveKpiChips`): colonne SLA in tabella. */
  const [slaEnabled, setSlaEnabled] = useState(false);
  const [processPage, setProcessPage] = useState(1);
  const [processPagination, setProcessPagination] = useState<StatisticsV2Pagination | null>(null);
  /**
   * Righe della lista **senza** il filtro SLA: e' il chip "Totale", che conta
   * tutto (con e senza SLA). Con un chip SLA selezionato la paginazione
   * risponde sul sottoinsieme filtrato, quindi il totale si legge a parte;
   * `null` significa "non applicabile o non ancora noto".
   */
  const [totalWithoutSlaFilter, setTotalWithoutSlaFilter] = useState<number | null>(null);
  const [lastBatchFull, setLastBatchFull] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(values.search);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setDebouncedSearch(values.search), 400);
    return () => window.clearTimeout(timeoutId);
  }, [values.search]);

  const [showAllTags, setShowAllTags] = useState(false);
  const [showAllGroupsFilter, setShowAllGroupsFilter] = useState(false);
  const [showAllRolesFilter, setShowAllRolesFilter] = useState(false);
  const [showAllPriorities, setShowAllPriorities] = useState(false);
  const [showAllRoles, setShowAllRoles] = useState(false);
  const [pageData, setPageData] = useState<PageData>({
    users: { data: [], loaded: false },
    robots: { data: [], loaded: false },
    groups: { data: [], loaded: false },
    processes: { data: [], loaded: false },
    roles: { data: [], loaded: false },
    processModels: { data: [], loaded: false },
    userDataKeys: { data: [], loaded: false },
    tags: { data: [], loaded: false },
    priorities: { data: [], loaded: false },
    allGroups: { data: [], loaded: false },
    allRoles: { data: [], loaded: false },
  });

  const processes = useMemo(() => pageData.processes.data, [pageData]);
  const users = useMemo(() => pageData.users.data, [pageData]);
  const hasLoadedProcesses = processes.length > 0;

  // The sort is translated once and reused as both a fetch input and a refetch
  // trigger. Depends on `hasLoadedProcesses` rather than on `processes` itself:
  // the userdata type is read from the loaded rows, but keying on the array
  // would make every appended page look like a sort change.
  //
  // ponytail: the type of a userdata key is only known once rows exist, so on a
  // mount that restores a userdata sort from localStorage the first request
  // goes out with type "string", the type is then re-read and page 1 is fetched
  // a second time. One redundant request, and only on that path — the fix (a
  // declared type travelling with the saved sort) is a bigger change than the
  // waste it removes.
  const sortParams = useMemo(
    () => newSortToApiSort(newSort, processes),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [newSort, hasLoadedProcesses],
  );

  // users is intentionally excluded: it's used at fetch-time via usersRef, not as a trigger
  const filterParamsKey = useMemo(
    () => JSON.stringify(buildStatisticsV2Params({ ...values, search: debouncedSearch }, [], history, 1, sortParams)),
    [values, debouncedSearch, history, sortParams],
  );

  const monitorScope = `${organization.id}:${history}:${calendarRevision}:${filterParamsKey}`;
  const monitorScopeRef = React.useRef(monitorScope);
  monitorScopeRef.current = monitorScope;
  const processRequestRef = React.useRef(0);

  // keep a ref so fetchProcesses always reads latest users without being a dep
  const usersRef = React.useRef(users);
  usersRef.current = users;

  const processModels = useMemo(() => pageData.processModels.data, [pageData]);
  const groups = useMemo(() => pageData.groups.data, [pageData]);
  const roles = useMemo(() => pageData.roles.data, [pageData]);
  const allUserDataKeys = useMemo(
    () => Array.from(new Set<string>([...pageData.userDataKeys.data, ...(values.userDataKeys || [])])),
    [pageData.userDataKeys.data, values.userDataKeys]
  );
  const allTags = useMemo(() => pageData.tags.data, [pageData]);
  const allPriorities = useMemo(() => pageData.priorities.data, [pageData]);
  const allGroupsFilter = useMemo(() => pageData.allGroups.data, [pageData]);
  const allRolesFilter = useMemo(() => pageData.allRoles.data, [pageData]);

  // Local fallback: ids of processes the current user has started in
  // this organization. TODO(BE): replace with `process.idstartuser`
  // once /statistics/current and /statistics/history expose it.
  const startedByMeIds = useMemo(() => {
    if (!user?.userId || !organization?.id) return new Set<number>();
    return new Set(getStartedByMeProcessIds(user.userId, organization.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId, organization?.id, processes.length]);

  const setUsers = (users: User[]) => {
    setPageData((prev) => ({ ...prev, users: { data: users, loaded: true } }));
  };
  const setGroups = (groups: Group[]) => {
    setPageData((prev) => ({
      ...prev,
      groups: { data: groups, loaded: true },
    }));
  };
  const setRoles = (roles: Role[]) => {
    setPageData((prev) => ({ ...prev, roles: { data: roles, loaded: true } }));
  };
  const setRobots = (robots: Robot[]) => {
    setPageData((prev) => ({
      ...prev,
      robots: { data: robots, loaded: true },
    }));
  };
  // FASE 1: arrivo sulla pagina
  // FASE 2: cambio organizzazione
  // FASE 3: esco dalla pagina

  // FASE 1: inizializzo filtri
  // FASE 2: modifico filtri

  const handleChangeSort = (key: string) => {
    setNewSort((_) => {
      switch (_[key]) {
        case undefined:
        case SortState.None:
          return { [key]: SortState.Desc };
        case SortState.Asc:
          return { [key]: SortState.None };
        case SortState.Desc:
          return { [key]: SortState.Asc };
      }
    });
  };

  // Sorting is server-side now: the API returns each page already ordered
  // (see newSortToApiSort / buildStatisticsV2Params), so this is the array as
  // it came back. If a sort key is ever added that the backend does not
  // support, re-add an explicit local sort here — not a silent one.
  const displayProcesses = processes;

  // Fetches process models from the org API (one-time, not from statistics).
  // Custom field options come from the server catalog; other metadata accumulates from rows.
  const fetchMetadata = useCallback(async () => {
    try {
      const { data } = await organizationsApi.get(`/${organization?.id}/processmodels/v2`);
      setPageData((prev) => ({
        ...prev,
        processModels: { data: data.processmodels ?? [], loaded: true },
      }));
    } catch (error) {
      console.error("Error fetching process models:", error);
    }
  }, [organization]);

  // page is passed explicitly so the closure never needs processPage in its deps.
  // This prevents stale-closure bugs when the filter-change effect calls fetch immediately
  // before the processPage state update is committed.
  const fetchProcesses = useCallback(async (page: number = 1) => {
    const requestId = ++processRequestRef.current;
    const isCurrentRequest = () => requestId === processRequestRef.current && monitorScope === monitorScopeRef.current;
    if (page === 1) {
      setLoading(true);
      setLoadingMore(false);
      setProcessPagination(null);
      setLastBatchFull(false);
      setPageData(prev => ({ ...prev, userDataKeys: { data: [], loaded: false } }));
    }
    else setLoadingMore(true);
    try {
      const filterValues = { ...values, search: debouncedSearch };
      const users = usersRef.current;

      if (shouldSkipStatisticsV2Fetch(filterValues, users)) {
        setProcessPagination(null);
        setPageData((prev) => ({
          ...prev,
          processes: { data: [], loaded: true },
          userDataKeys: { data: [], loaded: true },
        }));
        return [];
      }

      const params = buildStatisticsV2Params(filterValues, users, history, page, sortParams);
      let processesList: StartedProcess[];
      let pagination: StatisticsV2Pagination;
      let userDataKeys: string[];

      if (history) {
        const statisticsResponse = await fetchStatisticsV2History(organization.id, params);
        if (!isCurrentRequest()) return;
        processesList = mapHistoryV2ResponseToStartedProcesses(statisticsResponse.data);
        pagination = statisticsResponse.pagination;
        const keys = statisticsResponse.filter_options?.userDataKeys;
        userDataKeys = Array.isArray(keys) && keys.every(key => typeof key === 'string') ? keys : [];
        setLastBatchFull(statisticsResponse.data.length >= STATISTICS_V2_PROCESS_PAGE_SIZE);
      } else {
        const statisticsResponse = await fetchStatisticsV2Current(organization.id, params);
        if (!isCurrentRequest()) return;
        processesList = mapStatisticsV2TasksToStartedProcesses(statisticsResponse.data);
        pagination = statisticsResponse.pagination;
        const keys = statisticsResponse.filter_options?.userDataKeys;
        userDataKeys = Array.isArray(keys) && keys.every(key => typeof key === 'string') ? keys : [];
        setLastBatchFull(statisticsResponse.data.length >= STATISTICS_V2_PROCESS_PAGE_SIZE);
      }

      setProcessPagination(pagination);
      // KPI SLA dell'intestazione: si rileggono con la prima pagina della lista viva.
      if (page === 1 && !history) setSlaKpiRefreshKey((key) => key + 1);
      setPageData((prev) => {
        const isFirstPage = page === 1;
        const mergeStrings = (a: string[], b: string[]) =>
          isFirstPage ? b : Array.from(new Set([...a, ...b]));

        return {
          ...prev,
          // Accumulate rows across pages; reset on page 1 (filter change / fresh load).
          processes: {
            data: isFirstPage ? processesList : [...prev.processes.data, ...processesList],
            loaded: true,
          },
          userDataKeys: {
            data: userDataKeys,
            loaded: true,
          },
          tags: {
            data: isFirstPage
              ? getUniqueTags(processesList)
              : Array.from(new Map([...prev.tags.data, ...getUniqueTags(processesList)].map(t => [t.name, t])).values()),
            loaded: true,
          },
          priorities: {
            data: mergeStrings(prev.priorities.data, getUniqueValues(processesList, 'priorityname')),
            loaded: true,
          },
          allGroups: {
            data: mergeStrings(prev.allGroups.data, getUniqueValues(processesList, 'jsongroups')),
            loaded: true,
          },
          allRoles: {
            data: mergeStrings(prev.allRoles.data, getUniqueValues(processesList, 'jsonroles')),
            loaded: true,
          },
        };
      });
      return processesList;
    } catch (error) {
      if (!isCurrentRequest()) return;
      console.error("Error fetching processes:", error);
    } finally {
      if (isCurrentRequest()) {
        if (page === 1) setLoading(false);
        else setLoadingMore(false);
      }
    }
  // usersRef.current is read at call-time; processPage is passed explicitly as arg.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // sortParams IS a dependency: without it a sort change would not change this
  // callback's identity, and the refetch triggered by filterParamsKey would
  // still run the previous closure and send the old sort.
  }, [organization, history, values, debouncedSearch, sortParams, monitorScope]);

  // fetch degli utenti dell'organizzazione
  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await organizationsApi.get(`${organization.id}/users`);
      if (Array.isArray(data.users)) {
        setUsers(data.users);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    }
  }, [organization]);

  // useEffect(() => {
  //   // solo user che hanno modeli attivi
  //   const activeUserIds = new Set(
  //     processes
  //       .filter((process) => process.state === 1) // questi sono i processi in corso
  //       .map((process) => process.iduser) // e qua li mappiamo per utente
  //   );

  //   const activeUsers = users.filter((user) => activeUserIds.has(user.id));
  //   setFilteredUsers(activeUsers);
  // }, [users, processes]);

  useEffect(() => {
    setFilteredUsers(users);
  }, [users]);

  // fetch dei gruppi dell'organizzazione
  const fetchGroups = useCallback(async () => {
    try {
      const { data } = await organizationsApi.get(`${organization.id}/groups`);
      if (Array.isArray(data.groups)) {
        setGroups(data.groups);
      } else {
        setGroups([]);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
      setGroups([]);
    }
  }, [organization]);

  const fetchRoles = useCallback(async () => {
    try {
      const { data } = await organizationsApi.get(`${organization.id}/roles`);
      if (Array.isArray(data.roles)) {
        setRoles(data.roles);
      } else {
        setRoles([]);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      setRoles([]);
    }
  }, [organization]);

  const fetchRobots = useCallback(async () => {
    try {
      const { data } = await organizationsApi.get(`${organization.id}/robots`);
      if (Array.isArray(data.robots)) {
        setRobots(data.robots);
      } else {
        setRobots([]);
      }
    } catch (error) {
      setRobots([]);
    }
  }, [organization]);

  useEffect(() => {
    setNewSort({});
  }, [sortField]);

  useEffect(() => {
    fetchUsers();
    fetchGroups();
    fetchRoles();
    fetchRobots();
  }, [fetchUsers, fetchGroups, fetchRoles, fetchRobots]);

  useEffect(() => {
    fetchMetadata();
  }, [fetchMetadata]);

  // Always holds the latest fetchProcesses (avoids stale closures in callbacks).
  const fetchProcessesRef = React.useRef(fetchProcesses);
  fetchProcessesRef.current = fetchProcesses;

  // Whenever filters / org / history change: reset to page 1 and fetch fresh.
  useEffect(() => {
    setProcessPage(1);
    fetchProcessesRef.current(1);
    return () => { processRequestRef.current += 1; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterParamsKey, organization?.id, history, calendarRevision]);

  /**
   * Chip "Totale" con un chip SLA selezionato: una richiesta a parte, stessi
   * filtri di pagina **senza** `sla_health` e una sola riga (serve solo
   * `total_items`). Senza filtro SLA non si chiede nulla: il totale e' gia'
   * quello della paginazione della lista.
   */
  const totalWithoutSlaKey = useMemo(
    () =>
      JSON.stringify(
        buildStatisticsV2Params(
          { ...values, search: debouncedSearch, slaHealth: null },
          [],
          history,
          1,
        ),
      ),
    [values, debouncedSearch, history],
  );

  useEffect(() => {
    if (history || values.slaHealth == null) {
      setTotalWithoutSlaFilter(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const filterValues = { ...values, search: debouncedSearch, slaHealth: null };
      const users = usersRef.current;
      if (shouldSkipStatisticsV2Fetch(filterValues, users)) {
        if (!cancelled) setTotalWithoutSlaFilter(0);
        return;
      }
      try {
        const response = await fetchStatisticsV2Current(organization.id, {
          ...buildStatisticsV2Params(filterValues, users, false, 1),
          pagesize: 1,
        });
        if (!cancelled) setTotalWithoutSlaFilter(response.pagination.total_items);
      } catch {
        // Errore: il chip resta "—" invece di mostrare un totale sbagliato.
        if (!cancelled) setTotalWithoutSlaFilter(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalWithoutSlaKey, values.slaHealth, organization?.id, history, calendarRevision]);

  /** Conteggio del chip "Totale": tutte le righe, con e senza SLA. */
  const totalAllCount = useMemo(() => {
    if (values.slaHealth == null) {
      return processPagination?.total_items ?? displayProcesses.length;
    }
    return totalWithoutSlaFilter ?? undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.slaHealth, processPagination, displayProcesses.length, totalWithoutSlaFilter]);

  // Prefer the server's total_pages when it is a sensible value (> 0).
  // Otherwise fall back to "the last page came back full" heuristic so infinite
  // scroll still works if the backend doesn't paginate reliably (e.g. history v2).
  const hasMore =
    processPagination && processPagination.total_pages > 0
      ? processPage < processPagination.total_pages
      : lastBatchFull;

  const loadMore = useCallback(() => {
    if (!hasMore || loading || loadingMore) return;
    const nextPage = processPage + 1;
    setProcessPage(nextPage);
    fetchProcessesRef.current(nextPage);
  }, [hasMore, loading, loadingMore, processPage]);

  const handleClearFilters = useCallback(
    (disableLocalStorage = false, clearUserData = false) => {
      setValues((prev: any) => ({
        status: "current",
        modelids: [],
        activityids: [],
        userids: [],
        groupids: [],
        roleids: [],
        creatorids: [],
        search: "",
        userDataKeys: clearUserData ? [] : prev.userDataKeys,
        tags: [],
        groups: [],
        roles: [],
        priorities: [],
        dateRange: { start: null, end: null },
        slaHealth: null,
        startedByMe: false,
      }));
      if (!disableLocalStorage) {
        if (filtersStorageKey) {
          localStorage.removeItem(filtersStorageKey);
        }
        if (sortsStorageKey) {
          localStorage.removeItem(sortsStorageKey);
        }
        setNewSort({});
      }
    },
    [setValues, filtersStorageKey, sortsStorageKey]
  );

  // KPI SLA chips: toggle filter on the live list. Changing it changes
  // filterParamsKey, so the list goes back to page 1 like any other filter.
  const handleSelectSlaHealth = useCallback(
    (health: number | null) => {
      setValues((prev: any) => ({ ...prev, slaHealth: health }));
    },
    [setValues]
  );

  // SLA off (or its settings unreadable): a filter restored from localStorage
  // would hide rows with no visible chip to undo it, so it is dropped.
  const handleSlaAvailability = useCallback(
    (enabled: boolean) => {
      setSlaEnabled(enabled);
      if (enabled) return;
      setValues((prev: any) =>
        prev.slaHealth == null ? prev : { ...prev, slaHealth: null }
      );
    },
    [setValues]
  );

  const handleClearUserdata = useCallback(() => {
    setValues((prev: any) => ({
      ...prev,
      userDataKeys: [],
    }));
  }, [setValues]);

  useEffect(() => {
    const value = doing && toDo ? "current" : doing ? "doing" : "todo";
    changeHandler("status", value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doing, toDo]);

  const extractUniqueActivities = (processes: StartedProcess[]) => {
    // qua le estraiamo (le attività uniché sennò mi vengono i doppioni sul filtro)
    return Array.from(
      new Set(
        processes.map((process) =>
          process.flownodetype === 0 && process.idtype === 3
            ? getTranslation(translations, "events.intermediate_catch")
            : process.flownodename
        )
      )
    );
  };

  useEffect(() => {
    setActivities(extractUniqueActivities(processes));
  }, [processes]);

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
  };

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleCheckboxChange = (category: string, value: string | null) => {
    const currentCategoryValues = values[category] || [];
    changeHandler(
      category,
      currentCategoryValues.includes(value)
        ? currentCategoryValues.filter((v: string) => v !== value)
        : [...currentCategoryValues, value]
    );
  };

  const filterChips = useMemo(() => {
    const chips: { label: string; onDelete: () => void }[] = [];

    if (values.search?.trim()) {
      chips.push({
        label: values.search,
        onDelete: () => changeHandler("search", ""),
      });
    }

    values.modelids?.forEach((id: number) => {
      const model = processModels.find((m) => m.id === id);
      if (model) {
        chips.push({
          label: model.name,
          onDelete: () => changeHandler("modelids", values.modelids.filter((mId: number) => mId !== id)),
        });
      }
    });

    values.activityids?.forEach((activity: string) => {
      chips.push({
        label: activity || getTranslation(translations, "activity.no_name"),
        onDelete: () => changeHandler("activityids", values.activityids.filter((a: string) => a !== activity)),
      });
    });

    (Array.isArray(values.userids) ? values.userids : []).forEach((id: string) => {
      const user = filteredUsers.find((u) => String(u.id) === id);
      if (user) {
        chips.push({
          label: `${user.lastname} ${user.firstname}`,
          onDelete: () => changeHandler("userids", (Array.isArray(values.userids) ? values.userids : []).filter((uId: string) => uId !== id)),
        });
      }
    });

    (Array.isArray(values.creatorids) ? values.creatorids : []).forEach((id: number) => {
      const user = filteredUsers.find((u) => u.id === id);
      if (user) {
        chips.push({
          label: `${user.lastname} ${user.firstname}`,
          onDelete: () => changeHandler("creatorids", (Array.isArray(values.creatorids) ? values.creatorids : []).filter((cId: number) => cId !== id)),
        });
      }
    });

    values.userDataKeys?.forEach((key: string) => {
      chips.push({
        label: key,
        onDelete: () => changeHandler("userDataKeys", values.userDataKeys.filter((k: string) => k !== key)),
      });
    });

    values.tags?.forEach((tag: string) => {
      chips.push({
        label: tag,
        onDelete: () => changeHandler("tags", values.tags.filter((t: string) => t !== tag)),
      });
    });

    values.groups?.forEach((group: string) => {
      chips.push({
        label: group,
        onDelete: () => changeHandler("groups", values.groups.filter((g: string) => g !== group)),
      });
    });

    values.roles?.forEach((role: string) => {
      chips.push({
        label: role,
        onDelete: () => changeHandler("roles", values.roles.filter((r: string) => r !== role)),
      });
    });

    values.priorities?.forEach((priority: string) => {
      chips.push({
        label: priority,
        onDelete: () => changeHandler("priorities", values.priorities.filter((p: string) => p !== priority)),
      });
    });

    return chips;
  }, [values, processModels, filteredUsers, translations]);

  const processFilterCount = useMemo(() => {
    return (
      (values.modelids?.length || 0) +
      (values.activityids?.length || 0) +
      (values.creatorids?.length || 0)
    );
  }, [values]);

  const participantFilterCount =
    (values.groups?.length || 0) +
    (values.roles?.length || 0) +
    (values.userids?.length || 0);

  const collapsedFilterCount =
    processFilterCount +
    participantFilterCount +
    (values.tags?.length || 0) +
    (values.priorities?.length || 0);

  const isSearchActive = Boolean(values.search?.trim());

  const activeSortsCount = useMemo(() => {
    return Object.keys(newSort).filter((key) => newSort[key]).length;
  }, [newSort]);

  const [initializedFiltersStorageKey, setInitializedFiltersStorageKey] = useState<string | null>(null);
  const [initializedSortsStorageKey, setInitializedSortsStorageKey] = useState<string | null>(null);

  useEffect(() => {
    if (!filtersStorageKey || !sortsStorageKey) {
      setInitializedFiltersStorageKey(null);
      setInitializedSortsStorageKey(null);
      return;
    }
    const savedFilters = localStorage.getItem(filtersStorageKey);
    const parsed = savedFilters ? normalizeProcessFilters(customParse(savedFilters)) : null;
    if (parsed) {
      // Il filtro di salute SLA non si ripristina: entrando nella vista viva si
      // parte sempre da "Totale" (nessun filtro). Gli altri filtri restano persistiti.
      setValues((prev: any) => ({ ...prev, ...parsed, slaHealth: null }));
      if (parsed.status === "doing") {
        setDoing(true);
        setToDo(false);
      } else if (parsed.status === "todo") {
        setDoing(false);
        setToDo(true);
      } else {
        setDoing(true);
        setToDo(true);
      }
    } else {
      setValues(DEFAULT_PROCESS_FILTERS);
      setDoing(true);
      setToDo(true);
    }
    const savedSorts = localStorage.getItem(sortsStorageKey);
    if (savedSorts) {
      const parsedSorts = JSON.parse(savedSorts);
      setNewSort(parsedSorts);
    } else {
      setNewSort({});
    }
    setInitializedFiltersStorageKey(filtersStorageKey);
    setInitializedSortsStorageKey(sortsStorageKey);
  }, [setValues, filtersStorageKey, sortsStorageKey]);


  useEffect(() => {
    if (!filtersStorageKey || initializedFiltersStorageKey !== filtersStorageKey) return;

    localStorage.setItem(
      filtersStorageKey,
      JSON.stringify(values)
    );
  }, [values, filtersStorageKey, initializedFiltersStorageKey]);

  useEffect(() => {
    if (!sortsStorageKey || initializedSortsStorageKey !== sortsStorageKey) return;

    localStorage.setItem(
      sortsStorageKey,
      JSON.stringify(newSort)
    );
  }, [newSort, sortsStorageKey, initializedSortsStorageKey]);

  //anti-filter-breaker
  useEffect(() => {
    if (
      pageData.processModels.loaded &&
      pageData.groups.loaded &&
      pageData.roles.loaded &&
      pageData.processes.loaded &&
      pageData.userDataKeys.loaded
    ) {
      if (!filtersStorageKey) return;
      const processModels = pageData.processModels.data;
      const groups = pageData.groups.data;
      const roles = pageData.roles.data;
      const processes = pageData.processes.data;
      const values = normalizeProcessFilters(customParse(
        localStorage.getItem(filtersStorageKey) || ""
      ));
      if (values) {
        const validModelIds = new Set(processModels.map((model) => model.id));
        const validActivityIds = new Set(
          processes.map((process) => process.flownodename)
        );
        const validUserIds = new Set(
          processes.map((process) => String(process.iduser))
        );
        const validCreatorIds = new Set(
          pageData.users.data.map((u) => u.id)
        );
        const validGroupIds = new Set(groups.map((group) => String(group.id)));
        const validRoleIds = new Set(roles.map((role) => String(role.id)));
        // tags.data is LabelData[] — extract .name for string comparison
        const validTags = new Set(pageData.tags.data.map((t) => t.name));
        const validGroupsFilter = new Set(pageData.allGroups.data);
        const validRolesFilter = new Set(pageData.allRoles.data);
        const validPriorities = new Set(pageData.priorities.data);

        const filteredModelIds = values.modelids.filter((id: number) =>
          validModelIds.has(id)
        );
        const filteredActivityIds = values.activityids.filter((id: string) =>
          validActivityIds.has(id)
        );
        const filteredUserIds = (Array.isArray(values.userids) ? values.userids : []).filter((id: string) =>
          validUserIds.has(id)
        );
        const filteredCreatorIds = (Array.isArray(values.creatorids) ? values.creatorids : []).filter((id: number) =>
          validCreatorIds.has(id)
        );
        const filteredGroupIds = values.groupids.filter((id: string) =>
          validGroupIds.has(id)
        );
        const filteredRoleIds = values.roleids.filter((id: string) =>
          validRoleIds.has(id)
        );
        const filteredTags = (values.tags || []).filter((tag: string) =>
          validTags.has(tag as any)
        );
        const filteredGroupsFilter = (values.groups || []).filter((group: string) =>
          validGroupsFilter.has(group)
        );
        const filteredRolesFilter = (values.roles || []).filter((role: string) =>
          validRolesFilter.has(role)
        );
        const filteredPriorities = (values.priorities || []).filter((priority: string) =>
          validPriorities.has(priority)
        );

        if (
          filteredModelIds.length !== values.modelids.length ||
          filteredActivityIds.length !== values.activityids.length ||
          filteredUserIds.length !== values.userids.length ||
          filteredCreatorIds.length !== values.creatorids.length ||
          filteredGroupIds.length !== values.groupids.length ||
          filteredRoleIds.length !== values.roleids.length ||
          filteredTags.length !== (values.tags || []).length ||
          filteredGroupsFilter.length !== (values.groups || []).length ||
          filteredRolesFilter.length !== (values.roles || []).length ||
          filteredPriorities.length !== (values.priorities || []).length
        ) {
          setValues({
            ...values,
            modelids: filteredModelIds,
            activityids: filteredActivityIds,
            userids: filteredUserIds,
            creatorids: filteredCreatorIds,
            groupids: filteredGroupIds,
            roleids: filteredRoleIds,
            tags: filteredTags,
            groups: filteredGroupsFilter,
            roles: filteredRolesFilter,
            priorities: filteredPriorities,
          });
        }
      }
    }
  }, [pageData, setValues, filtersStorageKey]);

  return (
    <Stack height="100%" spacing={2} sx={{ minHeight: 0 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormGroup
            sx={{
              display: "flex",
              flexDirection: "row",
              // justifyContent: "space-between",
              flex: 1
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2, flex: 1 }}>
              {!history && (
                <FlowentiButton
                  id="start-process"
                  size="small"
                  variant="contained"
                  disabled={!bypassSubscription && !liveProcessCanUse}
                  sx={{ whiteSpace: "nowrap" }}
                  onClick={() => setStartProcessOpen(true)}
                >
                  {getTranslation(translations, "pm.startprocess.label.caption")}
                </FlowentiButton>
              )}
              <Box sx={!history ? { display: "flex", alignItems: "center" } : undefined}>
                {!history && (
                  <React.Fragment>
                    <FlowentiButton
                      id="switch-doing"
                      size="small"
                      variant="outlined"
                      sx={{
                        whiteSpace: "nowrap",
                        ...(doing ? STATUS_TOGGLE_ACTIVE_SX : STATUS_TOGGLE_INACTIVE_SX),
                      }}
                      startIcon={doing ? <CheckIcon fontSize="small" /> : undefined}
                      onClick={() => {
                        const nextDoingState = !doing;
                        if (!toDo && !nextDoingState) {
                          setToDo(true);
                        }
                        setDoing(nextDoingState);
                      }}
                    >
                      {getTranslation(
                        translations,
                        "pm.label.process_status.doing"
                      )}
                    </FlowentiButton>
                    <FlowentiButton
                      sx={{
                        mr: 1,
                        ml: 1,
                        whiteSpace: "nowrap",
                        ...(toDo ? STATUS_TOGGLE_ACTIVE_SX : STATUS_TOGGLE_INACTIVE_SX),
                      }}
                      id="switch-todo"
                      size="small"
                      variant="outlined"
                      startIcon={toDo ? <CheckIcon fontSize="small" /> : undefined}
                      onClick={() => {
                        const nextToDoState = !toDo;
                        if (!doing && !nextToDoState) {
                          setDoing(true);
                        }
                        setToDo(nextToDoState);
                      }}
                    >
                      {getTranslation(
                        translations,
                        "pm.label.process_status.todo"
                      )}
                    </FlowentiButton>
                  </React.Fragment>
                )}
                {isFilterCollapsed && (
                  <Badge
                    badgeContent={collapsedFilterCount}
                    color="primary"
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                  >
                    <FlowentiButton
                      sx={{ mr: 1 }}
                      id="collapsed-filter-menu"
                      onClick={(event) => setCollapsedFilterAnchorEl(event.currentTarget)}
                      variant="outlined"
                      startIcon={<FilterListIcon color="primary" />}
                    >
                      Filter
                    </FlowentiButton>
                  </Badge>
                )}
                <Badge
                  badgeContent={processFilterCount}
                  color="primary"
                  sx={{ display: isFilterCollapsed ? "none" : "inline-flex" }}
                  // sx={{ ml: 2 }}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                >
                  <FlowentiButton
                    sx={{ mr: 1 }}
                    id="filter-menu"
                    onClick={handleFilterClick}
                    variant="outlined"
                    startIcon={<AccountTreeIcon color="primary" />}
                  >
                    {getTranslation(translations, "pm.modelname.label.caption")}
                  </FlowentiButton>
                </Badge>
                <Badge
                  badgeContent={participantFilterCount}
                  color="primary"
                  sx={{ display: isFilterCollapsed ? "none" : "inline-flex" }}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                >
                  <FlowentiButton
                    sx={{ mr: 1 }}
                    id="participants-menu"
                    onClick={handleParticipantsClick}
                    variant="outlined"
                    startIcon={<PeopleIcon color="primary" />}
                  >
                    {getTranslation(translations, "generic.participants")}
                  </FlowentiButton>
                </Badge>
                <Badge
                  badgeContent={values.userDataKeys?.length}
                  color="primary"
                  // sx={{ ml: 2 }}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                >
                  <FlowentiButton
                    sx={{ mr: 1 }}
                    id="userdata-menu"
                    onClick={handleUserDataClick}
                    variant="outlined"
                    startIcon={<FormatListBulletedIcon color="primary" />}
                  >
                    {getTranslation(translations, "generic.userdata.caption")}
                  </FlowentiButton>
                </Badge>
                <Badge
                  badgeContent={values.tags?.length}
                  color="primary"
                  sx={{ display: isFilterCollapsed ? "none" : "inline-flex" }}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                >
                  <FlowentiButton
                    sx={{ mr: 1 }}
                    id="tags-menu"
                    onClick={handleTagsClick}
                    variant="outlined"
                    startIcon={<LabelOutlined color="primary" />}
                  >
                    {getTranslation(translations, "generic.labels.caption")}
                  </FlowentiButton>
                </Badge>
                <Badge
                  badgeContent={values.priorities?.length}
                  color="primary"
                  sx={{ display: isFilterCollapsed ? "none" : "inline-flex" }}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                >
                  <FlowentiButton
                    sx={{ mr: 1 }}
                    id="priorities-menu"
                    onClick={handlePrioritiesClick}
                    variant="outlined"
                    startIcon={<PriorityHighIcon color="primary" sx={{ mr: -1 }} />}
                  >
                    {getTranslation(translations, "generic.priorities.caption")}
                  </FlowentiButton>
                </Badge>
                <ProcessExportButton
                  processes={displayProcesses}
                  history={history}
                  title={getTranslation(
                    translations,
                    history ? "menu.left.history" : "generic.processes.monitor"
                  )}
                  onError={(message) =>
                    sendSnack({
                      type: "error",
                      message,
                    })
                  }
                />
                <Badge
                  badgeContent={activeSortsCount}
                  color="primary"
                  // sx={{ ml: 2 }}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                >
                  <FlowentiButton
                    id="sort-menu"
                    onClick={handleSortClick}
                    variant="outlined"
                    // sx={{ ml: 2 }}
                    startIcon={<SortIcon color="primary" />}
                  >
                    {getTranslation(translations, "generic.sort.caption")}
                  </FlowentiButton>
                </Badge>
                <TextField
                  onKeyDown={(e) => e.stopPropagation()}
                  variant="outlined"
                  size="small"
                  placeholder={getTranslation(translations, "pm.organizationchart.ricerca.placeholder")}
                  sx={{
                    ...(!history && { display: { xs: "none", sm: "block" } }),
                    width: 180,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "20px",
                      height: 42,
                      ml: 1,
                      backgroundColor: isSearchActive ? "rgba(84, 189, 193, 0.12)" : "transparent",
                      "& fieldset": {
                        borderColor: isSearchActive ? "primary.main" : undefined,
                        borderWidth: isSearchActive ? 2 : undefined,
                      },
                      "&:hover fieldset": {
                        borderColor: isSearchActive ? "primary.main" : undefined,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "primary.main",
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color={isSearchActive ? "primary" : "inherit"} />
                      </InputAdornment>
                    ),
                  }}
                  value={values.search}
                  onChange={(e) => {
                    e.preventDefault();
                    changeHandler("search", e.target.value);
                  }}
                />
              </Box>
              {history && (
                <DateRangePickerMenu
                  showDefaultIntervals
                  onChange={(newInterval) => {
                    changeHandler("dateRange", newInterval);
                  }}
                  value={values.dateRange}
                />
              )}
            </Box>
            {!history && (
              <TextField
                id="live-processes-mobile-search"
                onKeyDown={(e) => e.stopPropagation()}
                variant="outlined"
                size="small"
                fullWidth
                placeholder={getTranslation(translations, "pm.organizationchart.ricerca.placeholder")}
                sx={{ display: { xs: "block", sm: "none" }, mt: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color={isSearchActive ? "primary" : "inherit"} />
                    </InputAdornment>
                  ),
                }}
                value={values.search}
                onChange={(e) => changeHandler("search", e.target.value)}
              />
            )}
          </FormGroup>
        </Grid>
      </Grid>
      <Menu
        anchorEl={collapsedFilterAnchorEl}
        open={Boolean(collapsedFilterAnchorEl)}
        onClose={() => setCollapsedFilterAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <MenuItem onClick={() => handleCollapsedFilterSelect(setFilterAnchorEl)}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AccountTreeIcon color="primary" fontSize="small" />
            <Typography>{getTranslation(translations, "pm.modelname.label.caption")}</Typography>
          </Stack>
        </MenuItem>
        <MenuItem onClick={() => handleCollapsedFilterSelect(setParticipantsMenuAnchorEl)}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PeopleIcon color="primary" fontSize="small" />
            <Typography>{getTranslation(translations, "generic.participants")}</Typography>
          </Stack>
        </MenuItem>
        <MenuItem onClick={() => handleCollapsedFilterSelect(setTagsMenuAnchorEl)}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LabelOutlined color="primary" fontSize="small" />
            <Typography>{getTranslation(translations, "generic.labels.caption")}</Typography>
          </Stack>
        </MenuItem>
        <MenuItem onClick={() => handleCollapsedFilterSelect(setPrioritiesMenuAnchorEl)}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PriorityHighIcon color="primary" fontSize="small" />
            <Typography>{getTranslation(translations, "generic.priorities.caption")}</Typography>
          </Stack>
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={userDataMenuAnchorEl}
        open={Boolean(userDataMenuAnchorEl)}
        onClose={handleCloseUserData}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box p={2} component={Stack}>
          <FormControl component="fieldset">
            {allUserDataKeys.length > 0 && (
              <Box display="flex" alignItems="center" justifyContent="space-around">
                <Typography component="legend" sx={{ fontWeight: "bold" }}>
                  {getTranslation(translations, "generic.userdata.caption")}
                </Typography>
                <IconButton color="primary" onClick={() => handleClearUserdata()}>
                  <FilterListOffIcon />
                </IconButton>
              </Box>
            )}

            <Box sx={{ mt: 0.5, minWidth: 200 }}>
              <MultiSelectSearch
                options={allUserDataKeys.map((key) => ({
                  value: key,
                  label: key,
                }))}
                selected={values.userDataKeys || []}
                onChange={(newKeys) => changeHandler("userDataKeys", newKeys)}
                label={getTranslation(translations, "generic.search.userdata")}
              />
            </Box>
          </FormControl>
        </Box>
      </Menu>
      <Menu
        anchorEl={tagsMenuAnchorEl}
        open={Boolean(tagsMenuAnchorEl)}
        onClose={handleCloseTags}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box p={2} component={Stack}>
          <FormControl component="fieldset">
            {allTags.length > 0 && (
              <Box display="flex" alignItems="center" justifyContent="space-around">
                <Typography component="legend" sx={{ fontWeight: "bold" }}>
                  {getTranslation(translations, "generic.labels.caption")}
                </Typography>
                <IconButton color="primary" onClick={() => changeHandler("tags", [])}>
                  <FilterListOffIcon />
                </IconButton>
              </Box>
            )}
            <MuiFormGroup>
              {allTags
                .slice(0, showAllTags ? allTags.length : 5)
                .map((tag) => (
                  <Tooltip key={tag.id} title={tag.name}>
                    <FormControlLabel
                      slotProps={{
                        typography: {
                          sx: {
                            maxWidth: 150,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        },
                      }}
                      control={
                        <Checkbox
                          id={`tags-filter-${tag.name}`}
                          checked={values.tags?.includes(tag.name)}
                          onChange={() =>
                            handleCheckboxChange("tags", tag.name)
                          }
                          name={tag.name}
                        />
                      }
                      label={
                        <Labels
                          labelCompleteMode
                          labels={[tag]}
                        />
                      }
                    />
                  </Tooltip>
                ))}
              {allTags.length > 5 && (
                <Button
                  onClick={() => setShowAllTags(!showAllTags)}
                  variant="text"
                  sx={{ mt: 1, mr: 22 }}
                >
                  {showAllTags
                    ? getTranslation(translations, "generic.show.less")
                    : getTranslation(translations, "generic.show.all")}
                </Button>
              )}
            </MuiFormGroup>
          </FormControl>
        </Box>
      </Menu>
      <Menu
        anchorEl={participantsMenuAnchorEl}
        open={Boolean(participantsMenuAnchorEl)}
        onClose={handleCloseParticipants}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            maxHeight: "70vh",
            overflowY: "auto",
          },
        }}
      >
        <Box p={2} component={Stack} spacing={2}>
          <FormControl component="fieldset">
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography component="legend" sx={{ fontWeight: "bold" }}>
                {getTranslation(translations, "generic.participants")}
              </Typography>
              <IconButton size="small" color="primary" onClick={() => changeHandler("userids", [])}>
                <FilterListOffIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography component="legend" sx={{ fontWeight: "bold" }}>
                {getTranslation(translations, "generic.user")}
              </Typography>
            </Box>
            <Box sx={{ mt: 0.5, minWidth: 200 }}>
              <MultiSelectSearch
                options={filteredUsers.map((user) => ({
                  value: String(user.id),
                  label: `${user.lastname} ${user.firstname}`,
                }))}
                selected={values.userids}
                onChange={(newUserids) => changeHandler("userids", newUserids)}
                label={getTranslation(translations, "generic.search.user")}
              />
            </Box>
          </FormControl>
          {/* Groups Section */}
          <FormControl component="fieldset">
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography component="legend" sx={{ fontWeight: "bold" }}>
                {getTranslation(translations, "groups.name")}
              </Typography>
              {values.groups?.length > 0 && (
                <IconButton size="small" color="primary" onClick={() => changeHandler("groups", [])}>
                  <FilterListOffIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <MuiFormGroup>
              {allGroupsFilter
                .slice(0, showAllGroupsFilter ? allGroupsFilter.length : 5)
                .map((group) => (
                  <Tooltip key={group} title={group}>
                    <FormControlLabel
                      slotProps={{
                        typography: {
                          sx: {
                            maxWidth: 180,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        },
                      }}
                      control={
                        <Checkbox
                          id={`groups-filter-${group}`}
                          checked={values.groups?.includes(group)}
                          onChange={() =>
                            handleCheckboxChange("groups", group)
                          }
                          name={group}
                        />
                      }
                      label={group}
                    />
                  </Tooltip>
                ))}
              {allGroupsFilter.length > 5 && (
                <Button
                  onClick={() => setShowAllGroupsFilter(!showAllGroupsFilter)}
                  variant="text"
                  size="small"
                  sx={{ mt: 0.5, alignSelf: 'flex-start' }}
                >
                  {showAllGroupsFilter
                    ? getTranslation(translations, "generic.show.less")
                    : getTranslation(translations, "generic.show.all")}
                </Button>
              )}
            </MuiFormGroup>
          </FormControl>

          {/* Roles Section */}
          <FormControl component="fieldset">
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography component="legend" sx={{ fontWeight: "bold" }}>
                {getTranslation(translations, "roles.name")}
              </Typography>
              {values.roles?.length > 0 && (
                <IconButton size="small" color="primary" onClick={() => changeHandler("roles", [])}>
                  <FilterListOffIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <MuiFormGroup>
              {allRolesFilter
                .slice(0, showAllRolesFilter ? allRolesFilter.length : 5)
                .map((role) => (
                  <Tooltip key={role} title={role}>
                    <FormControlLabel
                      slotProps={{
                        typography: {
                          sx: {
                            maxWidth: 180,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        },
                      }}
                      control={
                        <Checkbox
                          id={`roles-filter-${role}`}
                          checked={values.roles?.includes(role)}
                          onChange={() =>
                            handleCheckboxChange("roles", role)
                          }
                          name={role}
                        />
                      }
                      label={role}
                    />
                  </Tooltip>
                ))}
              {allRolesFilter.length > 5 && (
                <Button
                  onClick={() => setShowAllRolesFilter(!showAllRolesFilter)}
                  variant="text"
                  size="small"
                  sx={{ mt: 0.5, alignSelf: 'flex-start' }}
                >
                  {showAllRolesFilter
                    ? getTranslation(translations, "generic.show.less")
                    : getTranslation(translations, "generic.show.all")}
                </Button>
              )}
            </MuiFormGroup>
          </FormControl>
        </Box>
      </Menu>
      <Menu
        anchorEl={prioritiesMenuAnchorEl}
        open={Boolean(prioritiesMenuAnchorEl)}
        onClose={handleClosePriorities}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box p={2} component={Stack}>
          <FormControl component="fieldset">
            {allPriorities.length > 0 && (
              <Box display="flex" alignItems="center" justifyContent="space-around">
                <Typography component="legend" sx={{ fontWeight: "bold" }}>
                  {getTranslation(translations, "generic.priorities.caption")}
                </Typography>
                <IconButton color="primary" onClick={() => changeHandler("priorities", [])}>
                  <FilterListOffIcon />
                </IconButton>
              </Box>
            )}
            <MuiFormGroup>
              {allPriorities
                .slice(0, showAllPriorities ? allPriorities.length : 5)
                .map((priority) => (
                  <Tooltip key={priority} title={priority}>
                    <FormControlLabel
                      slotProps={{
                        typography: {
                          sx: {
                            maxWidth: 150,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        },
                      }}
                      control={
                        <Checkbox
                          id={`priorities-filter-${priority}`}
                          checked={values.priorities?.includes(priority)}
                          onChange={() =>
                            handleCheckboxChange("priorities", priority)
                          }
                          name={priority}
                        />
                      }
                      label={priority}
                    />
                  </Tooltip>
                ))}
              {allPriorities.length > 5 && (
                <Button
                  onClick={() => setShowAllPriorities(!showAllPriorities)}
                  variant="text"
                  sx={{ mt: 1, mr: 22 }}
                >
                  {showAllPriorities
                    ? getTranslation(translations, "generic.show.less")
                    : getTranslation(translations, "generic.show.all")}
                </Button>
              )}
            </MuiFormGroup>
          </FormControl>
        </Box>
      </Menu>
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleCloseFilter}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box component={Stack} p={2}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography component="legend" sx={{ fontWeight: "bold" }}>
              {getTranslation(translations, "pm.modelname.label.caption")}
            </Typography>
            <IconButton color="primary" onClick={() => { changeHandler("modelids", []); changeHandler("activityids", []); changeHandler("creatorids", []); }}>
              <FilterListOffIcon />
            </IconButton>
          </Box>
          {filteredUsers.length > 0 && (
            <Box minWidth={200} sx={{ mt: 1 }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                fontWeight="bold"
              >
                {getTranslation(translations, "generic.filter.by.creator.caption")}
              </Typography>
              <MultiSelectSearch
                options={filteredUsers.map((u) => ({
                  value: String(u.id),
                  label: `${u.lastname} ${u.firstname}${user?.userId === u.id ? " (io)" : ""}`,
                }))}
                selected={(Array.isArray(values.creatorids) ? values.creatorids : []).map(String)}
                onChange={(newCreatorIds) => changeHandler("creatorids", newCreatorIds.map(Number))}
                label={getTranslation(translations, "generic.filter.by.creator.caption")}
              />
            </Box>
          )}
          <Box minWidth={200}>
            {processModels.length > 0 && (
              <Typography
                variant="subtitle1"
                gutterBottom
                marginTop={2}
                fontWeight="bold"
              >
                {getTranslation(translations, "generic.filter.by.processmodel")}
              </Typography>
            )}
            <MultiSelectSearch
              options={processModels.map((model) => ({
                value: String(model.id),
                label: model.name,
              }))}
              selected={values.modelids.map(String)}
              onChange={(newModels) =>
                changeHandler("modelids", newModels.map(Number))
              }
              label={getTranslation(
                translations,
                "generic.filter.by.processmodel"
              )}
            />
          </Box>

          <FormControl component="fieldset" sx={{ marginTop: 2 }}>
            {activities.length > 0 && (
              <Box sx={{ fontWeight: "bold" }}>
                {getTranslation(
                  translations,
                  "pm.associations.assigned_activity"
                )}
                <Box sx={{ mt: 0.5 }}>
                  <MultiSelectSearch
                    options={activities.map((activity) => ({
                      value: activity || "",
                      label: activity || getTranslation(translations, "activity.no_name"),
                    }))}
                    selected={values.activityids}
                    onChange={(newActivityids) => changeHandler("activityids", newActivityids)}
                    label={getTranslation(translations, "generic.search.activity")}
                  />
                </Box>
              </Box>
            )}
          </FormControl>
        </Box>
      </Menu>
      <Menu
        id="sort-menu"
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={handleSortClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box p={2} sx={{ minWidth: 250 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography sx={{ fontWeight: "bold" }}>
              {getTranslation(translations, "generic.sort.caption")}
            </Typography>
            {activeSortsCount > 0 && (
              <IconButton size="small" color="primary" onClick={() => setNewSort({})}>
                <FilterListOffIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          <Stack spacing={1}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body2">
                {getTranslation(translations, "generic.filter.data.start.process")}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleChangeSort("process")}
                sx={{ opacity: newSort.process ? 1 : 0.3 }}
              >
                {newSort.process === SortState.Asc ? (
                  <ArrowUpwardIcon fontSize="small" />
                ) : (
                  <ArrowDownwardIcon fontSize="small" />
                )}
              </IconButton>
            </Box>

            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body2">
                {getTranslation(translations, "generic.filter.data.start.activity")}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleChangeSort("activityStart")}
                sx={{ opacity: newSort.activityStart ? 1 : 0.3 }}
              >
                {newSort.activityStart === SortState.Asc ? (
                  <ArrowUpwardIcon fontSize="small" />
                ) : (
                  <ArrowDownwardIcon fontSize="small" />
                )}
              </IconButton>
            </Box>
          </Stack>
        </Box>
      </Menu>
      {/* ProcessesList stays mounted to avoid re-mounting useMonitor/usePriorities on every load.
          It renders its own skeleton rows under its real (sticky) header while `loading` is true,
          so the header never gets covered or duplicated. */}
      <Box sx={{ position: "relative", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <ProcessesList
          handleChangeSort={handleChangeSort}
          newSort={newSort}
          refresh={() => fetchProcessesRef.current(1)}
          title={getTranslation(
            translations,
            history ? "menu.left.history" : "generic.processes.monitor"
          )}
          processes={displayProcesses}
          filters={values}
          filterChips={filterChips}
          filteredUserDataKeys={values.userDataKeys}
          history={history}
          totalCount={processPagination?.total_items ?? displayProcesses.length}
          hasMore={hasMore}
          loadMore={loadMore}
          isLoadingMore={loadingMore}
          loading={loading}
          slaEnabled={!history && slaEnabled}
          slaRefreshKey={slaKpiRefreshKey}
          headerTitle={
            !history ? (
              <SlaLiveKpiChips
                idorganization={organization.id}
                refreshKey={slaKpiRefreshKey}
                selectedHealth={typeof values.slaHealth === "number" ? values.slaHealth : null}
                totalCount={totalAllCount}
                onSelectHealth={handleSelectSlaHealth}
                onAvailabilityChange={handleSlaAvailability}
                // Titolo e conteggio di sempre: con lo SLA spento l'intestazione
                // resta identica; con i KPI il chip "Totale" sostituisce il conteggio.
                fallback={
                  <>
                    <Typography variant="h6">
                      {getTranslation(translations, "generic.processes.monitor")}
                    </Typography>
                    <Chip
                      label={totalAllCount ?? displayProcesses.length}
                      sx={{ height: 30, fontSize: 15, fontWeight: 700 }}
                    />
                  </>
                }
              />
            ) : undefined
          }
        />
      </Box>
      <StartProcess
        isAdmin={organization.administrator}
        open={startProcessOpen}
        setOpen={setStartProcessOpen}
        onProcessStarted={async () => {
          await Promise.all([fetchMetadata(), fetchProcesses()]);
        }}
      />
    </Stack>
  );
};

export default ProcessStarted;
